import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Loading } from '../components/Loading';
import { EmptyState } from '../components/EmptyState';

// Fatia B — reivindicações "Este negócio é meu": o admin analisa o pedido,
// vê o WhatsApp de contato (wa.me) e decide APROVAR/REJEITAR.
const STATUS_META = {
  PENDING: { label: 'Pendente', color: 'var(--nc-warning-text)', bg: 'var(--nc-warning-soft)' },
  APPROVED: { label: 'Aprovada', color: 'var(--nc-success-text)', bg: 'var(--nc-success-soft)' },
  REJECTED: { label: 'Rejeitada', color: 'var(--nc-danger-text)', bg: 'var(--nc-danger-soft)' },
};

const FILTERS = [
  { value: 'PENDING', label: 'Pendentes' },
  { value: 'APPROVED', label: 'Aprovadas' },
  { value: 'REJECTED', label: 'Rejeitadas' },
];

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function AdminClaimsPage() {
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [filter, setFilter] = useState('PENDING');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [deciding, setDeciding] = useState(false);

  const fetchClaims = useCallback(async (status = 'PENDING') => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/claims', { params: { status, limit: 100 } });
      setClaims(data.data || []);
    } catch {
      setClaims([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClaims(filter); }, [filter, fetchClaims]);

  const decide = async (claim, decision) => {
    const ok = window.confirm(
      decision === 'APPROVE'
        ? `Aprovar a reivindicação de ${claim.claimer_name} para "${claim.business_name}"? Ele passará a administrar o comércio.`
        : `Rejeitar a reivindicação de ${claim.claimer_name} para "${claim.business_name}"?`
    );
    if (!ok) return;
    setDeciding(true);
    setMessage(null);
    try {
      await api.post(`/admin/claims/${claim.id}/decision`, { decision });
      setMessage({ type: 'ok', text: `Reivindicação ${decision === 'APPROVE' ? 'aprovada' : 'rejeitada'} com sucesso.` });
      const next = claims.filter(c => c.id !== claim.id);
      setClaims(next);
      setOpenId(null);
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.error?.message || 'Erro ao processar a decisão.' });
    } finally {
      setDeciding(false);
    }
  };

  const waLink = (wa) => (wa ? `https://wa.me/${wa.replace(/\D/g, '')}` : null);

  const meta = STATUS_META[filter] || STATUS_META.PENDING;

  return (
    <div className="nc-page">
      <div className="nc-pagehead">
        <div className="nc-row">
          <button onClick={() => navigate('/admin')} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="nc-page-title">Reivindicações</h1>
            <p style={{ fontSize: 11, opacity: 0.8, margin: '2px 0 0' }}>"Este negócio é meu" — sua decisão define o proprietário</p>
          </div>
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {/* Filtros */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              style={{
                flex: 1, padding: '9px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 600,
                background: filter === f.value ? '#0d5c3b' : '#fff',
                color: filter === f.value ? '#fff' : '#6b7280',
                border: filter === f.value ? 'none' : '1px solid var(--nc-border)'
              }}
            >
              {f.label}
              {f.value === 'PENDING' && claims.length > 0 && (
                <span style={{
                  background: filter === 'PENDING' ? '#fff' : '#fef3c7',
                  color: filter === 'PENDING' ? '#0d5c3b' : '#b45309',
                  borderRadius: 999, padding: '0 6px', marginLeft: 6, fontSize: 11
                }}>{claims.length}</span>
              )}
            </button>
          ))}
        </div>

        {message && (
          <div role="status" style={{
            padding: '10px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13,
            background: message.type === 'ok' ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${message.type === 'ok' ? '#bbf7d0' : '#fecaca'}`,
            color: message.type === 'ok' ? '#15803d' : '#b91c1c'
          }}>
            {message.text}
          </div>
        )}

        {loading ? <Loading /> : claims.length === 0 ? (
          <EmptyState
            icon="🏪"
            title={`Nenhuma reivindicação ${meta.label.toLowerCase()}`}
            description="Quando um usuário usar 'Este negócio é meu', os pedidos aparecem aqui."
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {claims.map(c => {
              const isOpen = openId === c.id;
              const link = waLink(c.contact_whatsapp);
              return (
                <div key={c.id} style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{
                      width: 42, height: 42, borderRadius: 10, background: 'var(--nc-success-soft)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0
                    }}>🏪</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)' }}>
                        {c.business_name}
                      </div>
                      <div className="nc-hint" style={{ marginTop: 2 }}>
                        Reivindicado por <strong>{c.claimer_name}</strong> ({c.claimer_email})
                      </div>
                      <div className="nc-caption" style={{ marginTop: 2 }}>
                        {c.business_address || 'Sem endereço'} • {formatDate(c.created_at)}
                      </div>
                    </div>
                    <button
                      onClick={() => setOpenId(isOpen ? null : c.id)}
                      aria-expanded={isOpen}
                      style={{ background: 'none', border: 'none', color: 'var(--nc-text-muted)', fontSize: 18, cursor: 'pointer', padding: 4 }}
                    >
                      {isOpen ? '−' : '+'}
                    </button>
                  </div>

                  {c.status === 'PENDING' && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button
                        onClick={() => decide(c, 'APPROVE')}
                        disabled={deciding}
                        style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: 'var(--nc-primary)', color: 'var(--nc-on-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                      >
                        ✓ Aprovar
                      </button>
                      <button
                        onClick={() => decide(c, 'REJECT')}
                        disabled={deciding}
                        style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid var(--nc-danger-border)', background: 'var(--nc-surface)', color: 'var(--nc-danger-text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                      >
                        ✕ Rejeitar
                      </button>
                    </div>
                  )}

                  {isOpen && (
                    <div style={{ borderTop: '1px solid var(--nc-border)', marginTop: 12, paddingTop: 12 }}>
                      {c.message && (
                        <div style={{ fontSize: 13, color: 'var(--nc-text-strong)', background: '#f9fafb', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                          <div className="nc-caption" style={{ fontWeight: 600, marginBottom: 4 }}>MENSAGEM DO REIVINDICANTE</div>
                          {c.message}
                        </div>
                      )}
                      <div className="nc-hint" style={{ marginBottom: 10 }}>
                        Negócio já possui dono: {c.business_owner_id ? `Sim (usuário #${c.business_owner_id})` : 'Não'}
                        {c.business_owner_id && ` • ${c.business_owner_id === c.claimer_id ? 'é o próprio reivindicante' : 'outro usuário'}`}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {link && (
                          <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              flex: 1, textAlign: 'center', padding: '10px 0', borderRadius: 8,
                              border: '1px solid var(--nc-success-border)', background: 'var(--nc-success-soft)', color: 'var(--nc-success-text)',
                              fontSize: 13, fontWeight: 600, textDecoration: 'none'
                            }}
                          >
                            💬 WhatsApp {c.contact_whatsapp || ''}
                          </a>
                        )}
                        {c.status !== 'PENDING' && (
                          <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: 12, color: 'var(--nc-text-muted)' }}>
                            {meta.label} • {c.decided_by_name ? `por ${c.decided_by_name}` : ''} em {formatDate(c.decided_at)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}